// ===========================================
// Extra Class Tab
// ===========================================
// Tab content for requesting extra classes

import { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { AppLocalizationProvider } from '../common/AppLocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as PendingIcon,
  Close as RejectIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  useExtraLessonRequests,
  useCreateExtraLessonRequest,
  useCancelExtraLessonRequest,
  useExtraLessonSettings,
  useTeacherAvailabilityDays,
  useAvailableTeacherSlots,
  useCreateDirectBooking,
} from '../../hooks/useBooking';
import {
  ExtraLessonRequest,
  ExtraLessonRequestStatus,
} from '../../api/booking.api';
import { useMyChildren } from '../../hooks/useParentProfile';
import { useStudentEnrollments } from '../../hooks/useLessons';
import { useTeachersForBooking } from '../../hooks/useUsers';
import { ParentFacingTeacher } from '../../api/users.api';
import { formatUTCDate, toLocalDateString } from '../../utils/dateUtils';

// ===========================================
// TYPES
// ===========================================

interface CreateRequestForm {
  studentId: string;
  instrumentId: string;
  lessonTypeId: string;
  reason: string;
  notes: string;
  // Direct booking fields
  teacherId: string;
  scheduledDate: Date | null;
  startTime: string;
  duration: number;
}

// ===========================================
// HELPERS
// ===========================================

const getStatusColor = (status: ExtraLessonRequestStatus) => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'info';
    case 'SCHEDULED':
      return 'primary';
    case 'COMPLETED':
      return 'success';
    case 'REJECTED':
      return 'error';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: ExtraLessonRequestStatus) => {
  switch (status) {
    case 'PENDING':
      return <PendingIcon fontSize="small" />;
    case 'APPROVED':
      return <CheckIcon fontSize="small" />;
    case 'SCHEDULED':
      return <ScheduleIcon fontSize="small" />;
    case 'COMPLETED':
      return <CheckIcon fontSize="small" />;
    case 'REJECTED':
      return <RejectIcon fontSize="small" />;
    case 'CANCELLED':
      return <CancelIcon fontSize="small" />;
    default:
      return null;
  }
};

const getStatusStep = (status: ExtraLessonRequestStatus): number => {
  switch (status) {
    case 'PENDING':
      return 0;
    case 'APPROVED':
      return 1;
    case 'SCHEDULED':
      return 2;
    case 'COMPLETED':
      return 3;
    case 'REJECTED':
    case 'CANCELLED':
      return -1;
    default:
      return 0;
  }
};

const formatTimeSlot = (startTime: string, endTime: string): string => {
  const formatTime = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  };
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

/** `HH:mm` + minutes → `HH:mm` (an extra lesson finishes before midnight). */
const endTimeOf = (startTime: string, durationMins: number): string => {
  const [h, m] = startTime.split(':').map(Number);
  const total = h * 60 + m + durationMins;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// ===========================================
// COMPONENT PROPS
// ===========================================

interface ExtraLessonTabProps {
  hideBookingsList?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function ExtraLessonTab({ hideBookingsList = false }: ExtraLessonTabProps) {
  // State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ExtraLessonRequest | null>(null);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateRequestForm>({
    studentId: '',
    instrumentId: '',
    lessonTypeId: '',
    reason: '',
    notes: '',
    teacherId: '',
    scheduledDate: null,
    startTime: '',
    duration: 60,
  });
  const [formErrors, setFormErrors] = useState<Partial<CreateRequestForm>>({});
  const [fallbackToRequest, setFallbackToRequest] = useState(false); // Track if we should fallback to request mode

  // Queries
  const { data: requests, isLoading: requestsLoading } = useExtraLessonRequests();
  const { data: children, isLoading: childrenLoading } = useMyChildren();
  const { data: studentEnrollments } = useStudentEnrollments(form.studentId);
  const { data: settings, isLoading: settingsLoading } = useExtraLessonSettings();

  // Determine booking mode
  const isDirectBooking = settings?.bookingType === 'DIRECT';

  // Only fetch teachers if in direct booking mode AND parent is allowed to select teacher
  const { data: teachers, isLoading: teachersLoading } = useTeachersForBooking({
    enabled: isDirectBooking && settings?.allowParentSelectTeacher === true
  });
  const { data: teacherAvailabilityDays = [] } = useTeacherAvailabilityDays(
    form.teacherId || null
  );
  const { data: availableSlots = [], isLoading: slotsLoading } = useAvailableTeacherSlots(
    form.teacherId || null,
    form.scheduledDate,
    form.duration
  );

  // Mutations
  const createRequest = useCreateExtraLessonRequest();
  const cancelRequest = useCancelExtraLessonRequest();
  const createDirectBooking = useCreateDirectBooking();

  // Categorize requests
  const { activeRequests, pastRequests } = useMemo(() => {
    const active: ExtraLessonRequest[] = [];
    const past: ExtraLessonRequest[] = [];

    (requests ?? []).forEach((req) => {
      if (req.status === 'COMPLETED' || req.status === 'REJECTED' || req.status === 'CANCELLED') {
        past.push(req);
      } else {
        active.push(req);
      }
    });

    // Sort by date
    active.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    past.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return { activeRequests: active, pastRequests: past.slice(0, 10) };
  }, [requests]);

  // Get available instruments and lesson types based on selected student
  // For extra lessons, only Individual lesson types are allowed
  const { availableInstruments, availableLessonTypes, individualLessonTypeId, studentTeacherId, studentTeacherName } = useMemo(() => {
    if (!studentEnrollments) {
      return {
        availableInstruments: [],
        availableLessonTypes: [],
        individualLessonTypeId: null,
        studentTeacherId: null,
        studentTeacherName: null,
      };
    }

    // Filter to only Individual lesson types
    const individualTypes = (studentEnrollments.lessonTypes ?? []).filter(
      (type) => type.name.toLowerCase() === 'individual'
    );

    // Get teacher ID and name from the selected instrument (for auto-assignment)
    const selectedInstrument = (studentEnrollments.instruments ?? []).find(
      (inst) => inst.id === form.instrumentId
    );

    return {
      availableInstruments: studentEnrollments.instruments ?? [],
      availableLessonTypes: individualTypes,
      individualLessonTypeId: individualTypes.length > 0 ? individualTypes[0].id : null,
      studentTeacherId: selectedInstrument?.teacherId || null,
      studentTeacherName: selectedInstrument?.teacherName || null,
    };
  }, [studentEnrollments, form.instrumentId]);

  // Auto-set teacher when instrument is selected and parent can't choose teacher
  useEffect(() => {
    if (
      isDirectBooking &&
      !settings?.allowParentSelectTeacher &&
      form.instrumentId &&
      studentTeacherId
    ) {
      // Only update if the teacher has changed to avoid infinite loops
      if (form.teacherId !== studentTeacherId) {
        setForm((prev) => ({ ...prev, teacherId: studentTeacherId }));
      }
    } else if (isDirectBooking && !settings?.allowParentSelectTeacher && !form.instrumentId) {
      // Clear teacher if no instrument is selected
      if (form.teacherId !== '') {
        setForm((prev) => ({ ...prev, teacherId: '' }));
      }
    }
  }, [isDirectBooking, settings?.allowParentSelectTeacher, form.instrumentId, studentTeacherId, form.teacherId]);

  // Handlers
  const handleOpenCreateDialog = () => {
    setForm({
      studentId: '',
      instrumentId: '',
      lessonTypeId: '',
      reason: '',
      notes: '',
      teacherId: '',
      scheduledDate: null,
      startTime: '',
      duration: settings?.defaultDurationMinutes || 60,
    });
    setFormErrors({});
    setFallbackToRequest(false); // Reset fallback state
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleFormChange = (field: keyof CreateRequestForm, value: string) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // When student changes, auto-select Individual lesson type if available
      if (field === 'studentId' && individualLessonTypeId) {
        updated.lessonTypeId = individualLessonTypeId;
      }

      // Note: Teacher auto-assignment is handled by useEffect below
      // to ensure we use the correct teacherId from the newly selected instrument

      return updated;
    });
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<CreateRequestForm> = {};

    if (!form.studentId) {
      errors.studentId = 'Please select a student';
    }

    if (isDirectBooking && !fallbackToRequest) {
      // Direct booking validation
      // Instrument is required when teacher auto-assignment is needed
      if (!settings?.allowParentSelectTeacher && !form.instrumentId) {
        errors.instrumentId = 'Please select an instrument';
      }
      if (settings?.allowParentSelectTeacher && !form.teacherId) {
        errors.teacherId = 'Please select a teacher';
      }
      if (!form.scheduledDate) {
        errors.scheduledDate = 'Please select a date' as any;
      }
      if (!form.startTime) {
        errors.startTime = 'Please select a time slot';
      }
    } else {
      // Request mode validation (or fallback to request)
      if (!form.reason || form.reason.length < 10) {
        errors.reason = 'Please provide a reason (at least 10 characters)';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitRequest = () => {
    if (!validateForm()) return;

    if (isDirectBooking && !fallbackToRequest) {
      // Direct booking mode
      if (availableSlots.length === 0) {
        alert('No available time slots. The teacher may not have availability configured. Please try the request mode or contact the school.');
        return;
      }

      createDirectBooking.mutate(
        {
          studentId: form.studentId,
          teacherId: form.teacherId,
          instrumentId: form.instrumentId,
          lessonTypeId: form.lessonTypeId,
          // Send the picked calendar DAY as a date-only YYYY-MM-DD string (from LOCAL components,
          // since the picker gave a local Date), matching every other booking path. Sending the
          // raw Date would serialise to a full UTC instant and could shift the day off-AEST.
          scheduledDate: toLocalDateString(form.scheduledDate!),
          startTime: form.startTime,
          duration: form.duration,
          notes: form.notes || undefined,
        },
        {
          onSuccess: () => {
            handleCloseCreateDialog();
          },
        }
      );
    } else {
      // Request mode (or fallback to request from direct booking)
      createRequest.mutate(
        {
          studentId: form.studentId,
          instrumentId: form.instrumentId || undefined,
          lessonTypeId: form.lessonTypeId || undefined,
          reason: form.reason,
          notes: form.notes || undefined,
        },
        {
          onSuccess: () => {
            handleCloseCreateDialog();
          },
        }
      );
    }
  };

  const handleOpenCancelDialog = (request: ExtraLessonRequest) => {
    setSelectedRequest(request);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedRequest(null);
  };

  const handleConfirmCancel = () => {
    if (!selectedRequest) return;

    cancelRequest.mutate(selectedRequest.id, {
      onSuccess: () => {
        handleCloseCancelDialog();
      },
    });
  };

  const toggleExpanded = (requestId: string) => {
    setExpandedRequestId((prev) => (prev === requestId ? null : requestId));
  };

  // Loading state
  const isLoading = requestsLoading || childrenLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with create button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">
            {isDirectBooking ? 'Extra Class Bookings' : 'Extra Class Requests'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isDirectBooking
              ? 'Book additional classes outside your regular schedule'
              : 'Request additional classes outside your regular schedule'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={!settings?.enabled}
        >
          {isDirectBooking ? 'Book Extra Class' : 'Request Extra Class'}
        </Button>
      </Box>

      {/* Info Alert */}
      {isDirectBooking ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Select a teacher and time slot to instantly book your extra class. Additional fees may apply.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Extra class requests go through an approval process. Once approved, the school will
          schedule your class and send you confirmation. Additional fees may apply.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Active Requests */}
        <Grid item xs={12} md={hideBookingsList ? 12 : 8}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Active Requests
          </Typography>

          {activeRequests.length === 0 ? (
            <Alert severity="info">
              No active requests. Click "Request Extra Class" to submit a new request.
            </Alert>
          ) : (
            <List>
              {activeRequests.map((request) => {
                const isExpanded = expandedRequestId === request.id;
                const statusStep = getStatusStep(request.status);

                return (
                  <Card key={request.id} sx={{ mb: 2 }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PersonIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle1">
                              {request.student?.firstName} {request.student?.lastName}
                            </Typography>
                            <Chip
                              label={request.status}
                              size="small"
                              color={getStatusColor(request.status)}
                              icon={getStatusIcon(request.status) || undefined}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {request.instrument?.name || 'Any instrument'}
                            {request.lessonType && ` - ${request.lessonType.name}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Requested {format(new Date(request.createdAt), 'd MMM yyyy')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {request.status === 'PENDING' && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenCancelDialog(request)}
                              title="Cancel request"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(request.id)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Progress Stepper */}
                      {statusStep >= 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Stepper activeStep={statusStep} alternativeLabel>
                            <Step completed={statusStep > 0}>
                              <StepLabel>Pending</StepLabel>
                            </Step>
                            <Step completed={statusStep > 1}>
                              <StepLabel>Approved</StepLabel>
                            </Step>
                            <Step completed={statusStep > 2}>
                              <StepLabel>Scheduled</StepLabel>
                            </Step>
                            <Step completed={statusStep > 3}>
                              <StepLabel>Completed</StepLabel>
                            </Step>
                          </Stepper>
                        </Box>
                      )}

                      {/* Expanded Details */}
                      <Collapse in={isExpanded}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ pl: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Reason
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            {request.reason}
                          </Typography>

                          {request.notes && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Additional Notes
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {request.notes}
                              </Typography>
                            </>
                          )}

                          {request.adminNotes && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Admin Notes
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {request.adminNotes}
                              </Typography>
                            </>
                          )}

                          {request.status === 'SCHEDULED' && (
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light', mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Scheduled Class
                              </Typography>
                              <Typography variant="body1">
                                {formatUTCDate(request.scheduledDate!, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                {', '}
                                {/* From–till: a make-up's length can differ from the class. */}
                                {request.scheduledTime}
                                {request.scheduledTime && request.duration
                                  ? `–${endTimeOf(request.scheduledTime, request.duration)}`
                                  : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {request.duration} minutes with {request.teacher?.user?.firstName}{' '}
                                {request.teacher?.user?.lastName}
                              </Typography>
                              {request.room && (
                                <Typography variant="caption" color="text.secondary">
                                  {request.room.location?.name} - {request.room.name}
                                </Typography>
                              )}
                            </Paper>
                          )}

                          {/* The server sends `staffNote` to a family ONLY when the
                              admin shared it — otherwise it is null. */}
                          {request.staffNote && (
                            <Paper variant="outlined" sx={{ p: 2, mt: 2 }} data-testid="family-note">
                              <Typography variant="subtitle2" gutterBottom>
                                Note from the school
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {request.staffNote}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </List>
          )}
        </Grid>

        {/* Past Requests - only show if not hidden */}
        {!hideBookingsList && (
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Past Requests
            </Typography>

            {pastRequests.length === 0 ? (
              <Alert severity="info">No past requests.</Alert>
            ) : (
              <List>
                {pastRequests.map((request) => (
                  <Paper key={request.id} sx={{ mb: 1, opacity: 0.8 }}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {request.student?.firstName} {request.student?.lastName}
                            </Typography>
                            <Chip
                              label={request.status}
                              size="small"
                              color={getStatusColor(request.status)}
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              {request.instrument?.name || 'Any instrument'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(request.createdAt), 'd MMM yyyy')}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}
          </Grid>
        )}
      </Grid>

      {/* Create Request Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isDirectBooking ? 'Book Extra Class' : 'Request Extra Class'}</DialogTitle>
        <DialogContent>
          {settingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth error={!!formErrors.studentId}>
                <InputLabel>Student *</InputLabel>
                <Select
                  value={form.studentId}
                  label="Student *"
                  onChange={(e) => handleFormChange('studentId', e.target.value)}
                >
                  {(children ?? []).map((child: { id: string; firstName: string; lastName: string }) => (
                    <MenuItem key={child.id} value={child.id}>
                      {child.firstName} {child.lastName}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.studentId && (
                  <Typography variant="caption" color="error">
                    {formErrors.studentId}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!formErrors.instrumentId}>
                <InputLabel>
                  Instrument {isDirectBooking && !settings?.allowParentSelectTeacher ? '*' : '(Optional)'}
                </InputLabel>
                <Select
                  value={form.instrumentId}
                  label={`Instrument ${isDirectBooking && !settings?.allowParentSelectTeacher ? '*' : '(Optional)'}`}
                  onChange={(e) => handleFormChange('instrumentId', e.target.value)}
                >
                  {(isDirectBooking && !settings?.allowParentSelectTeacher) ? null : (
                    <MenuItem value="">Any instrument</MenuItem>
                  )}
                  {availableInstruments.map((instrument: { id: string; name: string }) => (
                    <MenuItem key={instrument.id} value={instrument.id}>
                      {instrument.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.instrumentId && (
                  <Typography variant="caption" color="error">
                    {formErrors.instrumentId}
                  </Typography>
                )}
                {isDirectBooking && !settings?.allowParentSelectTeacher && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Required to assign your student's teacher
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Class Type</InputLabel>
                <Select
                  value={form.lessonTypeId}
                  label="Class Type"
                  onChange={(e) => handleFormChange('lessonTypeId', e.target.value)}
                  disabled={!form.studentId}
                >
                  {availableLessonTypes.length === 0 && !form.studentId && (
                    <MenuItem value="">Select a student first</MenuItem>
                  )}
                  {availableLessonTypes.length === 0 && form.studentId && (
                    <MenuItem value="">No individual lessons found</MenuItem>
                  )}
                  {availableLessonTypes.map((type: { id: string; name: string }) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {form.studentId && availableLessonTypes.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Extra lessons are only available as individual sessions
                  </Typography>
                )}
              </FormControl>

              {isDirectBooking ? (
                <>
                  {/* DIRECT BOOKING MODE */}
                  {settings?.allowParentSelectTeacher ? (
                    <FormControl fullWidth error={!!formErrors.teacherId}>
                      <InputLabel>Teacher *</InputLabel>
                      <Select
                        value={form.teacherId}
                        label="Teacher *"
                        onChange={(e) => handleFormChange('teacherId', e.target.value)}
                        disabled={teachersLoading}
                      >
                        {teachersLoading ? (
                          <MenuItem value="">Loading teachers...</MenuItem>
                        ) : (teachers ?? []).length === 0 ? (
                          <MenuItem value="">No teachers available</MenuItem>
                        ) : (
                          (teachers ?? []).map((teacher: ParentFacingTeacher) => (
                            <MenuItem key={teacher.id} value={teacher.id}>
                              {teacher.User.firstName} {teacher.User.lastName}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {formErrors.teacherId && (
                        <Typography variant="caption" color="error">
                          {formErrors.teacherId}
                        </Typography>
                      )}
                      {!teachersLoading && (teachers ?? []).length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                          No active teachers found. Please contact your school administrator.
                        </Typography>
                      )}
                    </FormControl>
                  ) : (
                    <Alert severity="info">
                      {studentTeacherName ? (
                        <>Teacher will be automatically assigned: <strong>{studentTeacherName}</strong></>
                      ) : form.instrumentId ? (
                        'No teacher found for this instrument. Please contact the school.'
                      ) : (
                        'Select an instrument to see which teacher will be assigned'
                      )}
                    </Alert>
                  )}

                  {form.teacherId && teacherAvailabilityDays.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This teacher has no availability configured. You can still request an extra lesson and an administrator will schedule it for you.
                    </Alert>
                  )}

                  <AppLocalizationProvider>
                    <DatePicker
                      label="Date *"
                      value={form.scheduledDate}
                      onChange={(date) => {
                        setForm((prev) => ({ ...prev, scheduledDate: date, startTime: '' }));
                        setFormErrors((prev) => ({ ...prev, scheduledDate: undefined } as any));
                      }}
                      shouldDisableDate={(date) => {
                        // Disable past dates
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) {
                          return true;
                        }

                        // If teacher is selected and has availability configured, only allow those days
                        if (form.teacherId && teacherAvailabilityDays.length > 0) {
                          const dayOfWeek = date.getDay();
                          return !teacherAvailabilityDays.includes(dayOfWeek);
                        }
                        // If no teacher selected or no availability, allow all future dates
                        return false;
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!(formErrors as any).scheduledDate,
                          helperText: (formErrors as any).scheduledDate || (
                            form.teacherId && teacherAvailabilityDays.length > 0
                              ? 'Only days when teacher is available are shown'
                              : undefined
                          ),
                        },
                      }}
                    />
                  </AppLocalizationProvider>

                  <FormControl fullWidth>
                    <InputLabel>Duration *</InputLabel>
                    <Select
                      value={form.duration}
                      label="Duration *"
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, duration: Number(e.target.value), startTime: '' }));
                      }}
                    >
                      <MenuItem value={30}>30 minutes</MenuItem>
                      <MenuItem value={45}>45 minutes</MenuItem>
                      <MenuItem value={60}>60 minutes</MenuItem>
                      <MenuItem value={90}>90 minutes</MenuItem>
                    </Select>
                  </FormControl>

                  {form.teacherId && form.scheduledDate && (
                    <>
                      {slotsLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Loading available time slots...
                          </Typography>
                        </Box>
                      ) : availableSlots.filter((slot: any) => slot.isAvailable).length > 0 && !fallbackToRequest ? (
                        <FormControl fullWidth error={!!formErrors.startTime}>
                          <InputLabel>Time Slot *</InputLabel>
                          <Select
                            value={form.startTime}
                            label="Time Slot *"
                            onChange={(e) => handleFormChange('startTime', e.target.value)}
                          >
                            {availableSlots
                              .filter((slot: any) => slot.isAvailable)
                              .map((slot: any) => (
                                <MenuItem key={slot.startTime} value={slot.startTime}>
                                  {formatTimeSlot(slot.startTime, slot.endTime)}
                                </MenuItem>
                              ))}
                          </Select>
                          {formErrors.startTime && (
                            <Typography variant="caption" color="error">
                              {formErrors.startTime}
                            </Typography>
                          )}
                        </FormControl>
                      ) : (
                        <Alert
                          severity="warning"
                          action={
                            !fallbackToRequest && (
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() => setFallbackToRequest(true)}
                              >
                                Submit Request
                              </Button>
                            )
                          }
                        >
                          {fallbackToRequest ? (
                            <>Submitting as a request. An administrator will schedule your lesson and contact you with the details.</>
                          ) : availableSlots.length === 0 ? (
                            <>{studentTeacherName || 'The teacher'} has no availability configured for this day. Click "Submit Request" to have an administrator schedule it for you.</>
                          ) : (
                            <>All time slots on this date are already booked. Please try a different date or click "Submit Request" to have an administrator schedule it for you.</>
                          )}
                        </Alert>
                      )}
                    </>
                  )}

                  {fallbackToRequest && (
                    <TextField
                      fullWidth
                      label="Reason for Request *"
                      value={form.reason}
                      onChange={(e) => handleFormChange('reason', e.target.value)}
                      multiline
                      rows={3}
                      error={!!formErrors.reason}
                      helperText={
                        formErrors.reason ||
                        'Please explain why you are requesting an extra class (e.g., exam preparation, catching up)'
                      }
                      placeholder="e.g., Preparing for upcoming music exam and would like extra practice time"
                    />
                  )}

                  <TextField
                    fullWidth
                    label="Notes (Optional)"
                    value={form.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    multiline
                    rows={2}
                    helperText="Any specific requirements or preferences"
                    placeholder="e.g., Prefer to focus on scales"
                  />
                </>
              ) : (
                <>
                  {/* REQUEST MODE */}
                  <TextField
                    fullWidth
                    label="Reason for Request *"
                    value={form.reason}
                    onChange={(e) => handleFormChange('reason', e.target.value)}
                    multiline
                    rows={3}
                    error={!!formErrors.reason}
                    helperText={
                      formErrors.reason ||
                      'Please explain why you are requesting an extra class (e.g., exam preparation, catching up)'
                    }
                    placeholder="e.g., Preparing for upcoming music exam and would like extra practice time"
                  />

                  <TextField
                    fullWidth
                    label="Additional Notes (Optional)"
                    value={form.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    multiline
                    rows={2}
                    helperText="Any specific requirements or preferences"
                    placeholder="e.g., Prefer afternoon slots, need to focus on scales"
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={createRequest.isPending || createDirectBooking.isPending || !settings?.enabled}
          >
            {(createRequest.isPending || createDirectBooking.isPending) ? (
              <CircularProgress size={20} />
            ) : isDirectBooking && !fallbackToRequest ? (
              'Book Now'
            ) : (
              'Submit Request'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog}>
        <DialogTitle>Cancel Request</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to cancel this extra class request?
          </Typography>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Student: {selectedRequest.student?.firstName} {selectedRequest.student?.lastName}
                <br />
                {selectedRequest.instrument?.name || 'Any instrument'}
                <br />
                Requested: {format(new Date(selectedRequest.createdAt), 'd MMM yyyy')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>Keep Request</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={cancelRequest.isPending}
          >
            {cancelRequest.isPending ? <CircularProgress size={20} /> : 'Cancel Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
